import { getSystemIcon, SystemIcon } from '@helpers/icons';
import { getSrcs } from '@helpers/image';
import { Device, until } from '@helpers/media';
import ArticleTags from '@stories/Components/Misc/ArticleTags/ArticleTags';
import { debounce } from 'lodash';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import S from './HowToGuide.styles';
const HowToGuide = (props) => {
    const sectionRef = React.useRef(null);
    const contentAreaRef = React.useRef(null);
    const buttonListRef = React.useRef(null);
    const buttonRef = React.useRef([]);
    const elementRef = React.useRef([]);
    const [scrollPercentage, setScrollPercentage] = React.useState(0);
    const [hideProgressBar, setHideProgressBar] = React.useState(false);
    const [activeMargin, setActiveMargin] = React.useState(120);
    const [activeSections, setActiveSections] = React.useState([]);
    const handleScroll = React.useCallback(debounce(() => {
        if (contentAreaRef.current) {
            const { top, height } = contentAreaRef.current.getBoundingClientRect();
            const totalScrollDistance = height - window.innerHeight;
            const percentage = ((top * -1) / totalScrollDistance) * 100;
            setScrollPercentage(Math.round(percentage < 0 ? 0 : percentage > 100 ? 100 : percentage));
        }
        if (sectionRef.current) {
            const { bottom } = sectionRef.current.getBoundingClientRect();
            setHideProgressBar(bottom < window.innerHeight);
        }
    }, 100), []);
    const checkActiveSections = () => {
        if (!props.contentItems || props.contentItems.length === 0) {
            return;
        }
        const active = props.contentItems.map((ci, index) => scrollPercentage === 100 ? index : !!ci.title && isSectionActive(index) ? index : undefined);
        if (!isEqual(active, activeSections)) {
            setActiveSections(active.filter((a) => a !== undefined));
        }
    };
    React.useEffect(() => {
        setTimeout(() => {
            document.querySelector('header')
                ? setActiveMargin((document.querySelector('header')?.clientHeight ?? 0) + 120)
                : setActiveMargin(120);
        }, 500);
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    React.useEffect(() => {
        checkActiveSections();
    }, [scrollPercentage]);
    React.useEffect(() => {
        if (activeSections.length === 0) {
            return;
        }
        // Get the last active section and ensure it is in view
        const activeIndex = activeSections[activeSections.length - 1];
        // Check if the bottom of last active button is visible within the buttonList
        const buttonListTop = buttonListRef.current?.getBoundingClientRect().top;
        const buttonListBottom = buttonListRef.current?.getBoundingClientRect().bottom;
        const buttonHeight = buttonRef.current[activeIndex].current?.clientHeight;
        const buttonY = buttonRef.current[activeIndex].current?.getBoundingClientRect().bottom;
        if (buttonY !== undefined &&
            buttonListTop !== undefined &&
            buttonListBottom !== undefined &&
            buttonHeight !== undefined) {
            const before = Math.floor(buttonY) < Math.floor(buttonListTop);
            const after = Math.floor(buttonY) > Math.floor(buttonListBottom);
            if (before || after) {
                buttonListRef.current?.scrollTo({
                    top: activeIndex === 0 ? 0 : before ? buttonY - buttonHeight : buttonY,
                    behavior: 'smooth',
                });
            }
        }
    }, [activeSections]);
    const isSectionActive = (index) => {
        if (elementRef.current[index]) {
            const top = elementRef.current[index].current?.getBoundingClientRect().top;
            return top !== undefined && top < activeMargin;
        }
        return false;
    };
    return (React.createElement(S.Container, { ref: sectionRef, "data-type": props.type, background: props.variant, translations: props.translations },
        React.createElement(S.SideMenuArea, { "data-hide": hideProgressBar ? 1 : 0 },
            React.createElement(S.SideMenu, null,
                React.createElement(S.SideTitle, null, props.title),
                React.createElement(S.ProgressBarHolder, null,
                    React.createElement(S.ProgressBar, { style: {
                            '--progressAmount': scrollPercentage + `%`,
                        } })),
                React.createElement(S.ButtonList, { ref: buttonListRef }, props.contentItems &&
                    props.contentItems.map((contentItem, index) => {
                        if (contentItem.title) {
                            return (React.createElement(S.SectionButton, { ref: (ref) => {
                                    buttonRef.current[index] = React.createRef();
                                    buttonRef.current[index].current = ref;
                                }, key: index, type: "button", "data-active": activeSections.includes(index) ? 1 : 0, onClick: () => {
                                    if (elementRef.current[index] && elementRef.current[index].current) {
                                        elementRef.current[index].current?.scrollIntoView({ behavior: 'smooth' });
                                    }
                                } },
                                contentItem.title,
                                React.createElement(S.ActiveSvg, null, getSystemIcon(SystemIcon.Tick))));
                        }
                        return null;
                    })))),
        React.createElement(S.GuideContent, { ref: contentAreaRef },
            React.createElement(S.GuideContentInner, null,
                React.createElement(S.IntroBlock, null,
                    React.createElement(ArticleTags, { id: props.id, tags: props.tags, readTime: props.readTime, type: props.type }),
                    React.createElement(S.TitleBlock, null,
                        React.createElement(S.Title, null, props.title),
                        props.introduction && React.createElement(S.Introduction, null, props.introduction))),
                props.introImage &&
                    renderImage(props.introImage, 'lazy', props.introImage.altText ?? props.title),
                props.contentItems &&
                    props.contentItems.map((contentItem, index) => (React.createElement(S.ContentItem, { key: index, ref: (ref) => {
                            elementRef.current[index] = React.createRef();
                            elementRef.current[index].current = ref;
                        } },
                        contentItem.title && React.createElement(S.ContentTitle, null, contentItem.title),
                        contentItem.content && (React.createElement(S.Content, { dangerouslySetInnerHTML: { __html: contentItem.content } })),
                        contentItem.image &&
                            renderImage(contentItem.image, 'lazy', contentItem.image.altText ?? contentItem.title))))))));
    function renderImage(image, loading, alt) {
        const imageSrc = getSrcs(image, 1714, 680, 8, 855 / 638);
        return (React.createElement(S.MediaHolder, null,
            React.createElement(S.Image, { src: imageSrc.src, srcSet: imageSrc.srcSet, alt: alt, loading: loading, sizes: `${until(Device.Tablet)} 100vw, ${until(Device.TabletLarge)} 80vw, ${until(Device.ActualDesktop)} 62.5vw, 855px` })));
    }
};
export default HowToGuide;
